import { CustomerGroupSettingsMessage } from '../../../../../../types/CustomerGroupSettingsMessage';
import { Button } from '../../../../../cdl/Button/Button';
import { Modal } from '../../../../../cdl/Modal/Modal';
import { ReadOnlyField } from '../../../../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../../../../cdl/Text/Text';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Box } from '../../../../../common/ui/Box';
import { useCustomerGroupSettings } from '../../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsUpdate } from '../../hooks/useCustomerGroupSettingsUpdate';

interface CustomerDisableSsoModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    groupId: string;
    groupName: string;
    connectionId: string;
}

export const CustomerDisableSsoModal = ({
    isOpen,
    onDismiss,
    groupId,
    groupName,
    connectionId,
}: CustomerDisableSsoModalProps) => {
    const settingsQuery = useCustomerGroupSettings(groupId);
    const updateMutation = useCustomerGroupSettingsUpdate(groupId);
    const { addToast, addErrorToast } = useToasts();

    const handleDisable = async () => {
        if (!settingsQuery.data || !settingsQuery.data.ssoConfiguration) return;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { connectionId, ...ssoConfigWithoutConnectionId } = settingsQuery.data.ssoConfiguration;
        const updatedSettings: CustomerGroupSettingsMessage = {
            ...settingsQuery.data,
            ssoConfiguration: ssoConfigWithoutConnectionId,
        };

        try {
            await updateMutation.mutateAsync({
                groupId,
                settings: updatedSettings,
            });
            addToast(translate('sso.disableSuccess', { groupName }));
            onDismiss();
        } catch {
            addErrorToast(translate('sso.disableError'));
        }
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss}>
            <Box display="flex" flexDirection="column" gap={24} padding={24}>
                <Box display="flex" flexDirection="column" gap={8}>
                    <Text variant="subtitle1" as="h2">
                        {translate('sso.disableSsoModal.title')}
                    </Text>
                    <Text variant="body" color="foreground.subtle">
                        {translate('sso.disableSsoModal.description')}
                    </Text>
                </Box>

                <ReadOnlyField label={translate('sso.disableSsoModal.connectionIdLabel')} value={connectionId} />

                <Box display="flex" justifyContent="flex-end" gap={8}>
                    <Button type="button" emphasis="medium" onClick={onDismiss}>
                        {translate('btn.cancel')}
                    </Button>
                    <Button emphasis="high" destructive onClick={handleDisable} isLoading={updateMutation.isPending}>
                        {translate('sso.disableSsoButton')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
